import { createContext, ReactElement, ReactNode, useContext, useMemo } from "react";
import { MesterplanApiLike, RealtimeClientLike } from "./types.js";

/**
 * Glue layer between the foundation packages and the actual web/mobile cluster
 * UI code. Mount one `<MesterplanClientsProvider>` inside the auth provider so
 * child components can read both clients via hooks:
 *
 *     <OidcAuthProvider config={...}>
 *       <MesterplanClientsProvider api={api} realtime={realtime}>
 *         <App />
 *       </MesterplanClientsProvider>
 *     </OidcAuthProvider>
 *
 * The clients are constructed by the app root from `@mesterplan/api-client`
 * + `@mesterplan/realtime-client`, parameterised by `useAuth().getToken`.
 */
interface ClientsContextValue {
  api: MesterplanApiLike;
  realtime: RealtimeClientLike;
}

const ClientsContext = createContext<ClientsContextValue | null>(null);

export interface MesterplanClientsProviderProps {
  api: MesterplanApiLike;
  realtime: RealtimeClientLike;
  children: ReactNode;
}

export function MesterplanClientsProvider(
  props: MesterplanClientsProviderProps,
): ReactElement {
  const value = useMemo<ClientsContextValue>(
    () => ({ api: props.api, realtime: props.realtime }),
    [props.api, props.realtime],
  );
  return (
    <ClientsContext.Provider value={value}>{props.children}</ClientsContext.Provider>
  );
}

export function useMesterplanApi(): MesterplanApiLike {
  const v = useContext(ClientsContext);
  if (v === null) {
    throw new Error("useMesterplanApi must be called inside <MesterplanClientsProvider>");
  }
  return v.api;
}

/**
 * Non-throwing variant: returns the API client when mounted inside a
 * <MesterplanClientsProvider>, or `null` otherwise. Use this at provider-wiring
 * sites that must keep working even when the .NET clients are not configured
 * (e.g. the local Firebase-only path where NetClientsProvider is absent).
 */
export function useOptionalMesterplanApi(): MesterplanApiLike | null {
  return useContext(ClientsContext)?.api ?? null;
}

export function useRealtimeClient(): RealtimeClientLike {
  const v = useContext(ClientsContext);
  if (v === null) {
    throw new Error("useRealtimeClient must be called inside <MesterplanClientsProvider>");
  }
  return v.realtime;
}
